import { C, GRADES } from '../constants';

export default function PeerAnalysis({ peers, subjectTicker }) {
  if (!peers?.length) return null;
  const subject = peers.find(p => p.isSubject || p.ticker===subjectTicker);
  const others  = peers.filter(p => !p.isSubject && p.ticker!==subjectTicker).sort((a,b)=>(b.dollarUpside||0)-(a.dollarUpside||0));
  const sorted  = subject ? [subject,...others] : others;

  // Pre-compute per-peer values for reuse in both desktop table and mobile chips
  const peerData = sorted.map(peer => {
    const isSubj   = peer.isSubject || peer.ticker===subjectTicker;
    const peerPrice= parseFloat(peer.currentPrice??peer.price??peer.current??peer.stockPrice??peer.marketPrice??0)||null;
    const peerFair = parseFloat(peer.fairValue??peer.intrinsicValue??peer.targetPrice??0)||null;
    const peerDU   = parseFloat(peer.dollarUpside??peer.upside??peer.upsideDownside??(peerFair&&peerPrice?peerFair-peerPrice:null)??0)||null;
    const up       = (peerDU||0) >= 0;
    const g        = GRADES[peer.grade] || GRADES["C"];
    const pct      = peer.percentUpside!=null ? parseFloat(peer.percentUpside)
                   : (peerDU&&peerPrice ? (peerDU/peerPrice)*100 : null);
    return { peer, isSubj, peerPrice, peerFair, peerDU, up, g, pct };
  });

  return (
    <div style={{ marginBottom:16 }}>
      <style>{`
        .hma-peer-table-wrap { display:block }
        .hma-peer-chips-row  { display:none }
        @media(max-width:768px){
          .hma-peer-table-wrap { display:none !important }
          .hma-peer-chips-row  { display:flex !important }
        }
        .hma-peer-chips-row::-webkit-scrollbar { display:none }
      `}</style>
      <div className="hma-section-label">Peer Comparison</div>
      <div style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:16, overflow:"hidden" }}>

        {/* Desktop: full table */}
        <div className="hma-peer-table-wrap" style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:480 }}>
            <thead>
              <tr>
                {["Ticker","Current","Fair Value","Upside $","Upside %","Grade"].map(h=>(
                  <th key={h} style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:C.hint, padding:"8px 16px", textAlign:"left", borderBottom:`1px solid ${C.bord}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {peerData.map(({ peer, isSubj, peerPrice, peerFair, peerDU, up, g, pct }, i) => (
                <tr key={i} style={{ background:isSubj?"rgba(201,168,76,0.04)":"transparent", borderLeft:isSubj?`3px solid ${C.gold2}`:"3px solid transparent" }}>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:800, color:isSubj?C.gold2:C.text }}>{peer.ticker}</span>
                      {isSubj && <span style={{ fontSize:11, fontWeight:700, padding:"1px 6px", borderRadius:10, background:"rgba(201,168,76,0.15)", color:C.gold2 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize:10, color:C.hint, marginTop:1 }}>{peer.name}</div>
                  </td>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none", fontFamily:"'Rajdhani',sans-serif", fontSize:14, color:C.soft }}>{peerPrice?`$${peerPrice.toFixed(2)}`:"—"}</td>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none", fontFamily:"'Rajdhani',sans-serif", fontSize:14, color:up?C.green:C.neg }}>{peerFair?`$${peerFair.toFixed(2)}`:"—"}</td>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none", fontFamily:"'Rajdhani',sans-serif", fontSize:15, fontWeight:800, color:up?C.green:C.neg }}>{peerDU!=null?`${up?"+":"-"}$${Math.abs(peerDU).toFixed(2)}`:"—"}</td>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none", fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:700, color:up?C.green:C.neg }}>{pct!=null?`${up?"+":""}${Number(pct).toFixed(1)}%`:"—"}</td>
                  <td style={{ padding:"11px 16px", borderBottom:i<peerData.length-1?`1px solid ${C.bord}`:"none" }}><span style={{ fontSize:12, fontWeight:800, padding:"3px 9px", borderRadius:6, background:g.bg, color:g.color }}>{peer.grade||"—"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: horizontal scrollable chip row */}
        <div
          className="hma-peer-chips-row"
          style={{
            flexDirection:"row",
            overflowX:"auto",
            gap:8,
            padding:"12px 14px 14px",
            scrollbarWidth:"none",
            WebkitOverflowScrolling:"touch",
          }}
        >
          {peerData.map(({ peer, isSubj, pct, up, g }, i) => (
            <div
              key={i}
              style={{
                display:"inline-flex",
                alignItems:"center",
                gap:6,
                flexShrink:0,
                background:isSubj?"rgba(201,168,76,0.08)":"rgba(255,255,255,0.05)",
                border:`1px solid ${isSubj?"rgba(201,168,76,0.45)":"rgba(201,168,76,0.2)"}`,
                borderRadius:999,
                padding:"8px 14px",
                minHeight:44,
              }}
            >
              {/* Ticker */}
              <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:700, color:isSubj?C.gold2:C.text }}>
                {peer.ticker}
                {isSubj && <span style={{ fontSize:11, fontWeight:700, marginLeft:4, color:C.gold2 }}>★</span>}
              </span>
              {/* Arrow + upside % */}
              {pct!=null && (
                <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:700, color:up?C.green:C.neg }}>
                  {up?"↑":"↓"}{up?"+":""}{Number(pct).toFixed(1)}%
                </span>
              )}
              {/* Grade badge */}
              <span style={{ fontSize:11, fontWeight:800, padding:"2px 7px", borderRadius:6, background:g.bg, color:g.color, flexShrink:0 }}>
                {peer.grade||"—"}
              </span>
            </div>
          ))}
        </div>

        <div style={{ padding:"8px 18px", borderTop:`1px solid ${C.bord}`, fontFamily:"'Space Mono',monospace", fontSize:11, color:C.hint }}>
          Peer fair values are P/E approximations.
          <span style={{ color:C.gold2, fontWeight:600, marginLeft:4, cursor:"pointer" }}>Run a full analysis on any ticker →</span>
        </div>
      </div>
    </div>
  );
}
